/**
 *
 * Home
 *
 */

import React, { memo, useEffect } from "react";
import { Helmet } from "react-helmet";
import { compose } from "redux";
import classNames from "classnames";
import { useTranslation} from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Row, Col, Typography } from "antd";

import { useAuthRole } from "utils/hooks";

// components
import HomePieChart from "components/Charts/HomePieChart";
import HomeLineChart from "components/Charts/HomeLineChart";
import HomeLineChart2 from "components/Charts/HomeLineChart2";
import AbsenceInfo from "components/Charts/AbsenceInfo";
import QuickLinks from "components/Charts/QuickLinks";
import AppVersionInformation from "components/AppVersionInformation";
import ButtonCardIcon from "components/ButtonCardIcon";

// global user
import { withUser } from "engine/Contexts/User.context";
import FeatureNotAvailable from "components/FeatureNotAvailable";

export function Home({
  // states
  history,
  user,
  //rest
  className,
}) {
  const childClassNames = classNames("home-page-desktop", className);
  const { t } = useTranslation();

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    user.getAmountAbsence();
    // Anything in here is fired on component unmount.
    return () => {};
  }, []);

  // render functions
  return (
    <div className={childClassNames}>
      <Helmet>
        <title>{t("layouts.navigation.home")}</title>
        <meta name="description" content="Description of Home" />
      </Helmet>
      {!!useAuthRole(["admin"]) && (
        <>
          <Row className="absences">
            <Col span={24}>
              <Typography.Title level={5} className="header-title">
                {t("labels.absences")}
              </Typography.Title>
            </Col>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={9}>
                  <FeatureNotAvailable margin={"0 8px"}>
                    <HomeLineChart />
                  </FeatureNotAvailable>
                </Col>
                <Col span={9}>
                  <FeatureNotAvailable margin={"0 8px"}>
                    <HomePieChart />
                  </FeatureNotAvailable>
                </Col>
                <Col span={6}>
                  <AbsenceInfo user={user} />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Row>
                <Col span={24}>
                  <Typography.Title level={5} className="header-title">
                    {t("labels.communications")}
                  </Typography.Title>
                </Col>
                <Col span={24}>
                  <FeatureNotAvailable>
                    <HomeLineChart2 />
                  </FeatureNotAvailable>
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24}>
                  <Typography.Title level={5} className="header-title">
                    {t("common.quickLinks")}
                  </Typography.Title>
                </Col>
                <Col span={24}>
                  <QuickLinks />
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}
      {!!useAuthRole(["teacher"]) && (
        <div className="quickly-link teacher">
          <h3 className="quickly-link__title">{t("common.quickLinks")}</h3>
          <div className="quickly-link__wrapper-btn">
            <ButtonCardIcon
              title={t("layouts.navigation.news")}
              icon="letter-close"
              className="wrapper-btn-link-button"
              action={() => {
                history.push({
                  pathname: `/communications`,
                });
              }}
            />
            <ButtonCardIcon
              title={t("labels.audiences")}
              icon="group"
              className="wrapper-btn-link-button"
              action={() => {
                history.push({
                  pathname: `/audiences`,
                });
              }}
            />
          </div>
        </div>
      )}
      <AppVersionInformation label="Version:" authenticated={true} />
    </div>
  );
}

export default compose(
  memo,
  withUser,
)(Home);
